import React, { useEffect, useState } from 'react';
import {
  Input, InputNumber, Select, Switch, Divider, Typography, Row, Col, Radio, Flex,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import AlumlyButton from '../../../components/AlumlyButton';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';

export interface FeaturedContentFormData {
  type: 'event' | 'news';
  eventId: number | null;
  newsId: number | null;
  displayOrder: number;
  isPublished: boolean;
}

interface Props {
  mode: 'create' | 'edit';
  featuredContentId?: string;
  defaultValues?: FeaturedContentFormData;
}

interface OptionItem {
  id: number;
  titleML: { en?: string; th?: string };
  coverImageUrl?: string;
  [key: string]: any;
}

const defaultFormValues: FeaturedContentFormData = {
  type: 'event',
  eventId: null,
  newsId: null,
  displayOrder: 0,
  isPublished: false,
};

const FormFeaturedContent: React.FC<Props> = ({ mode, featuredContentId, defaultValues }) => {
  const navigate = useNavigate();
  const { service: createService } = useServices(ServiceNameEnum.featuredContent.create);
  const { service: editService } = useServices(ServiceNameEnum.featuredContent.edit);
  const { service: eventsService } = useServices(ServiceNameEnum.featuredContent.publishedEvents);
  const { service: newsService } = useServices(ServiceNameEnum.featuredContent.publishedNews);

  const [eventOptions, setEventOptions] = useState<OptionItem[]>([]);
  const [newsOptions, setNewsOptions] = useState<OptionItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<OptionItem | null>(null);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<FeaturedContentFormData>({
    defaultValues: defaultValues || defaultFormValues,
  });

  const watchType = watch('type');

  const loadOptions = async (): Promise<void> => {
    const [evtRes, newsRes] = await Promise.all([
      eventsService.get({}),
      newsService.get({}),
    ]);
    if (evtRes.data) setEventOptions(evtRes.data);
    if (newsRes.data) setNewsOptions(newsRes.data);

    const eventId = defaultValues?.eventId;
    const newsId = defaultValues?.newsId;
    if (eventId && evtRes.data) {
      setSelectedItem(evtRes.data.find((e: OptionItem) => e.id === eventId) || null);
    } else if (newsId && newsRes.data) {
      setSelectedItem(newsRes.data.find((n: OptionItem) => n.id === newsId) || null);
    }
  };

  useEffect(() => { loadOptions(); }, []);

  const currentOptions = watchType === 'event' ? eventOptions : newsOptions;

  const getOptionLabel = (opt: OptionItem): string => opt.titleML?.en || opt.titleML?.th || `#${opt.id}`;

  const onSubmit = async (formData: FeaturedContentFormData): Promise<void> => {
    const payload = {
      type: formData.type,
      eventId: formData.type === 'event' ? formData.eventId : null,
      newsId: formData.type === 'news' ? formData.newsId : null,
      displayOrder: Number(formData.displayOrder) || 0,
      isPublished: formData.isPublished,
    };

    if (mode === 'create') {
      const { data, error } = await createService.post(payload);
      if (data) { toast.success('Featured content created'); navigate(RouteNameEnum.featuredContent.index); }
      else if (error) { toast.error(AxiosService.getErrorResponse(error)?.message, { toastId: 'allToast' }); }
    } else {
      const { data, error } = await editService.put(payload, { params: { id: featuredContentId } });
      if (data) { toast.success('Featured content updated'); navigate(RouteNameEnum.featuredContent.index); }
      else if (error) { toast.error(AxiosService.getErrorResponse(error)?.message, { toastId: 'allToast' }); }
    }
  };

  const selectError = watchType === 'event' ? errors.eventId : errors.newsId;

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 32 }}>
      {/* Type */}
      <HeaderLabel label="Content Type" />
      <div style={{ background: 'white', padding: 16 }}>
        <Controller name="type" control={control}
          render={({ field }) => (
            <div>
              <div style={{ marginBottom: 8 }}>Select type to feature</div>
              <Radio.Group
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('eventId', null);
                  setValue('newsId', null);
                  setSelectedItem(null);
                }}
              >
                <Radio value="event">Event</Radio>
                <Radio value="news">News</Radio>
              </Radio.Group>
            </div>
          )}
        />
      </div>

      {/* Select Item */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label={watchType === 'event' ? 'Select Event' : 'Select News'} />
        <div style={{ background: 'white', padding: 16 }}>
          <Controller
            name={watchType === 'event' ? 'eventId' : 'newsId'}
            control={control}
            rules={{ required: `Please select a ${watchType}` }}
            render={({ field, fieldState }) => (
              <Select
                showSearch
                size="large"
                style={{ width: '100%' }}
                placeholder={`Search ${watchType}...`}
                status={fieldState.error ? 'error' : ''}
                value={selectedItem ? selectedItem.id : undefined}
                optionFilterProp="label"
                onChange={(val) => {
                  const newValue = currentOptions.find((o) => o.id === val) || null;
                  setSelectedItem(newValue);
                  field.onChange(newValue?.id || null);
                }}
                options={currentOptions.map((option) => ({
                  value: option.id,
                  label: getOptionLabel(option),
                }))}
              />
            )}
          />
          {selectError && <ErrorMessage>{selectError.message}</ErrorMessage>}

          {selectedItem && (
            <div style={{ marginTop: 16, padding: 16, background: '#f9f9f9', borderRadius: 4 }}>
              <Typography.Text type="secondary">Selected Preview</Typography.Text>
              <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                {selectedItem.coverImageUrl && (
                  <Col flex="none">
                    <img src={selectedItem.coverImageUrl} alt=""
                      style={{ width: 180, height: 120, borderRadius: 4, objectFit: 'cover' }} />
                  </Col>
                )}
                <Col flex="auto">
                  <Typography.Text strong>{selectedItem.titleML?.en || '-'}</Typography.Text>
                  <div><Typography.Text type="secondary">{selectedItem.titleML?.th || '-'}</Typography.Text></div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>

      {/* Display Order */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Display Order" />
        <div style={{ background: 'white', padding: 16 }}>
          <Controller name="displayOrder" control={control}
            render={({ field }) => (
              <InputNumber
                value={field.value}
                onChange={field.onChange}
                placeholder="Display Order"
                min={0}
                size="large"
                style={{ width: 200 }}
              />
            )}
          />
        </div>
      </div>

      {/* Visibility */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Visibility" />
        <div style={{ background: 'white', padding: 16 }}>
          <Controller name="isPublished" control={control}
            render={({ field }) => (
              <Flex align="center" gap={8}>
                <Switch checked={field.value} onChange={field.onChange} />
                <span>Published (visible in app)</span>
              </Flex>
            )}
          />
        </div>
      </div>

      <Divider style={{ marginTop: 32, marginBottom: 32 }} />
      <Flex justify="center" gap={16}>
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.featuredContent.index)}>Cancel</AlumlyButton>
        <AlumlyButton label="rxv" width={200} type="submit">{mode === 'create' ? 'Create' : 'Save Changes'}</AlumlyButton>
      </Flex>
    </form>
  );
};

FormFeaturedContent.defaultProps = { featuredContentId: undefined, defaultValues: undefined };
export default FormFeaturedContent;
